/**
 * Gift Card Conversion Form Handler
 * Handles converting old gift cards to new Shopify gift cards
 * Uses FormValidator, ErrorUtils, TranslationUtils, and Delegate
 */

class GiftCardConvertForm extends HTMLElement {
  constructor() {
    super();
    this.delegateElement = new Delegate(this);
    this.formValidator = null;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.cacheElements();
    this.initFormValidator();
    this.bindEvents();
  }

  cacheElements() {
    this.formSection = this.querySelector('.redeem-form');
    this.codeInput = this.querySelector('#old-giftcard-code');
    this.submitButton = this.querySelector('[data-action="convert-giftcard"]');
  }

  initFormValidator() {
    if (!this.formSection || !this.codeInput) {
      console.warn('Gift Card Convert: Form elements not found');
      return;
    }

    // Initialize FormValidator with same behavior as redeem form
    this.formValidator = new FormValidator(this.formSection, {
      validateOnInput: false,
      errorClass: 'field--error',
      fieldSelector: '.field',
      buttonSelector: '#convert-submit-btn',
      errorOutputSelector: this.formSection.querySelector('.input-validation'),
      showErrorsOnSubmitOnly: false
    });

    // Add custom validator for the gift card code
    if (!this.codeInput.id) {
      this.codeInput.id = 'old-giftcard-code';
    }

    this.formValidator.addValidator(
      this.codeInput.id,
      this.validateGiftCardCode.bind(this),
      ''
    );

    console.log('Gift Card Convert: FormValidator initialized');
  }

  bindEvents() {
    // Submit button click
    this.delegateElement.on('click', '[data-action="convert-giftcard"]', this.handleSubmit.bind(this));
    
    // Allow Enter key to submit
    this.delegateElement.on('keydown', '#old-giftcard-code', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleSubmit(event);
      }
    });

    console.log('Gift Card Convert: Event delegation setup complete');
  }

  validateGiftCardCode(value) {
    // Same validation as redeem form
    if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
      return TranslationUtils.get(
        'gift_cards.convert.error_invalid_chars',
        'Invalid characters in gift card code'
      );
    }
    // Validate based on code length without spaces (old gift cards have 10 chars)
    const codeWithoutSpaces = value.replace(/\s/g, '');
    if (codeWithoutSpaces.length < 10) {
      return TranslationUtils.get(
        'gift_cards.convert.error_too_short',
        'Gift card code must be at least 10 characters'
      );
    }
    return true;
  }

  setLoading(loading) {
    this.isSubmitting = loading;
    
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      
      // Handle button loader
      const buttonText = this.submitButton.querySelector('.text_container');
      const buttonLoader = this.submitButton.querySelector('.spinningLoader');
      
      if (loading) {
        this.submitButton.classList.add('button--loading');
        if (buttonText) buttonText.style.opacity = '0';
        if (buttonLoader) buttonLoader.style.display = 'flex';
      } else {
        this.submitButton.classList.remove('button--loading');
        if (buttonText) buttonText.style.opacity = '1';
        if (buttonLoader) buttonLoader.style.display = 'none';
      }
    }

    if (this.codeInput) {
      this.codeInput.disabled = loading;
    }
  }

  async handleSubmit(event) {
    if (event) {
      event.preventDefault();
    }

    // Prevent double submission
    if (this.isSubmitting) {
      console.log('Gift Card Convert: Submission already in progress');
      return;
    }

    // Validate form using FormValidator
    if (!this.formValidator) {
      console.error('Gift Card Convert: FormValidator not initialized');
      return;
    }

    const isValid = this.formValidator.validateForm(false); // false = show errors
    if (!isValid) {
      console.log('Gift Card Convert: Form validation failed');
      return;
    }

    const code = this.codeInput?.value || '';
    this.setLoading(true);

    try {
      // Call the conversion API
      const endpoint = `${window.proxyPath || '/apps/proxy'}/legacy-converter`;
      const response = await this.makeApiCall(endpoint, {
        code: code.trim()
      });

      if (response.success) {
        // Show success toast
        const successMessage = response.message || TranslationUtils.get(
          'gift_cards.convert.success_toast',
          'Your old gift card has been successfully converted! Check your email for the new gift card.'
        );
        ErrorUtils.success(
          'gift_cards.convert',
          'success_toast',
          successMessage,
          5000
        );
        
        // Clear the form
        if (this.codeInput) {
          this.codeInput.value = '';
        }
        
        // Clear any validation errors
        if (this.formValidator) {
          this.formValidator.clearErrors();
        }
      } else {
        // Handle errors based on error code
        this.handleError(response);
      }
    } catch (error) {
      console.error('Gift Card Convert: Error during conversion', error);
      
      // Show network error toast
      const networkMessage = TranslationUtils.get(
        'gift_cards.convert.error_network',
        'Network error. Please check your connection and try again.'
      );
      ErrorUtils.error(
        'gift_cards.convert',
        'error_network',
        networkMessage,
        7000
      );
    } finally {
      this.setLoading(false);
    }
  }

  handleError(response) {
    const errorCode = response.code || 'UNKNOWN';
    const errorMessage = response.message;

    console.log('Gift Card Convert: Error', errorCode, errorMessage);

    // Special case: Unauthorized - redirect to login
    if (errorCode === 'UNAUTHORIZED') {
      const loginMessage = errorMessage || TranslationUtils.get(
        'gift_cards.convert.error_unauthorized',
        'You must be logged in to convert gift cards'
      );
      ErrorUtils.error(
        'gift_cards.convert',
        'error_unauthorized',
        loginMessage,
        3000
      );
      
      // Redirect to login after short delay
      setTimeout(() => {
        const returnUrl = encodeURIComponent(window.location.pathname);
        window.location.href = `/account/login?return_url=${returnUrl}`;
      }, 1000);
      return;
    }

    // Get user-friendly message based on error code
    const displayMessage = this.getErrorMessage(errorCode, errorMessage);
    
    // Show error toast
    ErrorUtils.error(
      'gift_cards.convert',
      'error_toast',
      displayMessage,
      7000
    );
  }

  getErrorMessage(code, defaultMessage) {
    const errorMessages = {
      'CUSTOMER_FETCH_ERROR': TranslationUtils.get(
        'gift_cards.convert.error_customer_fetch',
        'Unable to verify your account. Please try again or contact support.'
      ),
      'CUSTOMER_NO_EMAIL': TranslationUtils.get(
        'gift_cards.convert.error_customer_no_email',
        'Unable to verify your account information. Please contact support.'
      ),
      'VALIDATION_ERROR': TranslationUtils.get(
        'gift_cards.convert.error_validation',
        'Gift card code must be at least 10 characters (letters and numbers only)'
      ),
      'GIFT_CARD_NOT_FOUND': TranslationUtils.get(
        'gift_cards.convert.error_not_found',
        'Gift card not found. Please check your code and try again.'
      ),
      'OLD_GIFTCARD_ALREADY_USED': TranslationUtils.get(
        'gift_cards.convert.error_already_used',
        'This gift card has already been converted. Check your email for the new gift card.'
      ),
      'GIFT_CARD_EXPIRED': TranslationUtils.get(
        'gift_cards.convert.error_expired',
        'This gift card has expired and cannot be converted.'
      ),
      'CONVERSION_FAILED': TranslationUtils.get(
        'gift_cards.convert.error_conversion_failed',
        'An error occurred while converting your gift card. Please try again or contact support.'
      ),
      'NETWORK_ERROR': TranslationUtils.get(
        'gift_cards.convert.error_network',
        'Network error. Please check your connection and try again.'
      )
    };

    // Use provided message if available, otherwise use translation
    return defaultMessage || errorMessages[code] || TranslationUtils.get(
      'gift_cards.convert.error_unknown',
      'An unexpected error occurred. Please try again or contact support.'
    );
  }

  async makeApiCall(endpoint, data) {
    // Use ApiService if available, otherwise use fetch
    if (window.apiService && typeof window.apiService.post === 'function') {
      return await window.apiService.post(endpoint, data);
    }

    // Fallback to fetch
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      // Try to parse error response
      try {
        const errorData = await response.json();
        return errorData;
      } catch (e) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    }

    return await response.json();
  }

  showSuccess(response) {
    // Hide form and error sections
    if (this.formSection) {
      this.formSection.hidden = true;
    }
    if (this.errorSection) {
      this.errorSection.hidden = true;
    }

    // Populate success details
    const data = response.data || {};
    const newGiftCard = data.newGiftCard || {};
    
    const successMessage = this.successSection?.querySelector('.gift-card-convert__success-message');
    if (successMessage) {
      successMessage.textContent = response.message || TranslationUtils.get(
        'gift_cards.convert.success_message',
        'Your old gift card has been successfully converted!'
      );
    }

    // Set masked code
    const maskedCodeField = this.successSection?.querySelector('[data-field="masked-code"]');
    if (maskedCodeField) {
      maskedCodeField.textContent = newGiftCard.maskedCode || '-';
    }

    // Set balance
    const balanceField = this.successSection?.querySelector('[data-field="balance"]');
    if (balanceField && newGiftCard.balance && newGiftCard.currency) {
      const formattedBalance = this.formatMoney(newGiftCard.balance, newGiftCard.currency);
      balanceField.textContent = formattedBalance;
    }

    // Set expiry (hide if null)
    const expiryField = this.successSection?.querySelector('[data-field="expiry"]');
    const expiryWrapper = this.successSection?.querySelector('[data-field-wrapper="expiry"]');
    if (expiryField && newGiftCard.expiresOn) {
      expiryField.textContent = this.formatDate(newGiftCard.expiresOn);
      if (expiryWrapper) {
        expiryWrapper.hidden = false;
      }
    } else if (expiryWrapper) {
      expiryWrapper.hidden = true;
    }

    // Show success section
    if (this.successSection) {
      this.successSection.hidden = false;
    }

    // Scroll to success section
    this.successSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  showError(response) {
    // Hide form and success sections
    if (this.formSection) {
      this.formSection.hidden = true;
    }
    if (this.successSection) {
      this.successSection.hidden = true;
    }

    // Get error message based on error code
    const errorCode = response.code || 'UNKNOWN';
    const errorMessage = this.getErrorMessage(errorCode, response.error);

    // Show error toast
    ErrorUtils.error(
      'gift_cards.convert',
      'error_toast',
      errorMessage,
      7000
    );

    const errorMessageEl = this.errorSection?.querySelector('.gift-card-convert__error-message');
    if (errorMessageEl) {
      errorMessageEl.textContent = errorMessage;
    }

    // Show error section
    if (this.errorSection) {
      this.errorSection.hidden = false;
    }

    // Scroll to error section
    this.errorSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getErrorMessage(code, defaultMessage) {
    const errorMessages = {
      'UNAUTHORIZED': TranslationUtils.get(
        'gift_cards.convert.error_unauthorized',
        'You must be logged in to convert gift cards'
      ),
      'GIFT_CARD_NOT_FOUND': TranslationUtils.get(
        'gift_cards.convert.error_not_found',
        'Gift card not found. Please check your code and try again.'
      ),
      'OLD_GIFTCARD_ALREADY_USED': TranslationUtils.get(
        'gift_cards.convert.error_already_used',
        'This gift card has already been converted.'
      ),
      'GIFT_CARD_EXPIRED': TranslationUtils.get(
        'gift_cards.convert.error_expired',
        'This gift card has expired and cannot be converted.'
      ),
      'VALIDATION_ERROR': defaultMessage || TranslationUtils.get(
        'gift_cards.convert.error_validation',
        'Invalid gift card code format.'
      ),
      'CONVERSION_FAILED': TranslationUtils.get(
        'gift_cards.convert.error_conversion_failed',
        'Something went wrong. Please try again or contact support.'
      ),
      'NETWORK_ERROR': TranslationUtils.get(
        'gift_cards.convert.error_network',
        'Network error. Please check your connection and try again.'
      )
    };

    return errorMessages[code] || defaultMessage || TranslationUtils.get(
      'gift_cards.convert.error_unknown',
      'An unexpected error occurred. Please try again.'
    );
  }

  resetForm(event) {
    if (event) {
      event.preventDefault();
    }

    console.log('Gift Card Convert: Resetting form');

    // Clear input
    if (this.codeInput) {
      this.codeInput.value = '';
      this.codeInput.disabled = false;
    }

    // Clear validation errors
    if (this.formValidator) {
      this.formValidator.clearErrors();
    }

    // Reset button state
    if (this.submitButton) {
      this.submitButton.disabled = false;
    }

    // Show form, hide success and error
    if (this.formSection) {
      this.formSection.hidden = false;
    }
    if (this.successSection) {
      this.successSection.hidden = true;
    }
    if (this.errorSection) {
      this.errorSection.hidden = true;
    }

    // Focus on input
    setTimeout(() => {
      this.codeInput?.focus();
    }, 100);

    // Scroll to form
    this.formSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  formatMoney(amount, currency) {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return amount;

      // Try to use Shopify.formatMoney if available
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        const cents = Math.round(numAmount * 100);
        const moneyFormat = window.theme?.moneyFormat || '{{amount}} {{currency}}';
        return Shopify.formatMoney(cents, moneyFormat);
      }

      // Fallback to basic formatting
      return `${numAmount.toFixed(2)} ${currency}`;
    } catch (e) {
      return `${amount} ${currency}`;
    }
  }

  formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  }

  disconnectedCallback() {
    console.log('Gift Card Convert: Element disconnected, cleaning up');
    
    // Cleanup FormValidator
    if (this.formValidator) {
      this.formValidator = null;
    }

    // Delegate cleanup is automatic
  }
}

// Register custom element
if (typeof customElements !== 'undefined' && !customElements.get('gift-card-convert-form')) {
  customElements.define('gift-card-convert-form', GiftCardConvertForm);
}
